<template>
  <div class="audience">
    <header class="head">
      <h2 class="section-title">Audience</h2>
      <RangeTabs :options="RANGES" :value="range" :callback="setRange" label="Période de l'audience" />
    </header>
    <div v-if="hasFailed && !stats" class="notice">L'audience n'a pas pu être chargée.</div>
    <div v-else-if="!stats" class="notice">Chargement…</div>
    <template v-else>
      <p class="note">{{ note }}</p>
      <div class="kpis">
        <StatisticsKpi v-for="kpi in stats.kpis" :key="kpi.label" :label="kpi.label" :value="cell(kpi.kind, kpi.value)" :hint="kpi.hint ?? ''" :tone="toneOf(kpi.tone)" />
      </div>
      <div class="charts">
        <StatisticsCard :span="12" :title="stats.chart.title" :empty="!hasChartData">
          <StatisticsChart :labels="stats.chart.labels" :datasets="stats.chart.datasets" value-format="integer" />
        </StatisticsCard>
        <StatisticsCard v-if="stats.tables.groups" :span="12" :title="stats.tables.groups.title" subtitle="Pages vues et utilisateurs différents par groupe">
          <ActivityTable :table="stats.tables.groups" empty-text="Aucun groupe actif sur la période." />
        </StatisticsCard>
        <StatisticsCard v-for="card in breakdownCards" :key="card.id" :span="card.span" :title="card.title" :subtitle="card.subtitle" :empty="!card.data.length">
          <StatisticsChart :type="card.type" :labels="card.labels" :datasets="[{label: 'Sessions', data: card.data}]" :horizontal="card.type === 'bar'" :legend="card.type === 'doughnut'" value-format="integer" height="240px" />
        </StatisticsCard>
        <StatisticsCard :span="6" :title="stats.hours.title" subtitle="Pages vues par heure (heure de Paris)" :empty="!hasData(stats.hours.datasets[0].data)">
          <StatisticsChart :labels="stats.hours.labels" :datasets="stats.hours.datasets" value-format="integer" height="240px" />
        </StatisticsCard>
        <StatisticsCard :span="6" :title="stats.weekdays.title" subtitle="Pages vues par jour de la semaine" :empty="!hasData(stats.weekdays.datasets[0].data)">
          <StatisticsChart :labels="stats.weekdays.labels" :datasets="stats.weekdays.datasets" value-format="integer" height="240px" />
        </StatisticsCard>
        <StatisticsCard :span="12" title="Rapidité d'affichage de la première page" subtitle="Temps entre la demande de la page et son affichage, mesuré dans le navigateur de l'utilisateur">
          <p v-if="!stats.load.samples" class="muted">Pas encore de mesure.</p>
          <dl v-else class="facts">
            <div><dt>Médiane</dt><dd>{{ cell('milliseconds', stats.load.p50) }}</dd></div>
            <div><dt>75 % des visites</dt><dd>{{ cell('milliseconds', stats.load.p75) }}</dd></div>
            <div><dt>95 % des visites</dt><dd>{{ cell('milliseconds', stats.load.p95) }}</dd></div>
            <div><dt>Mesures</dt><dd>{{ cell('number', stats.load.samples) }}</dd></div>
          </dl>
        </StatisticsCard>
      </div>
    </template>
  </div>
</template>

<script>
import StatisticsKpi from "@/statistics/components/StatisticsKpi.vue";
import StatisticsCard from "@/statistics/components/StatisticsCard.vue";
import StatisticsChart from "@/statistics/components/StatisticsChart.vue";
import ActivityTable from "@/administration/components/ActivityTable.vue";
import RangeTabs from "@/administration/components/RangeTabs.vue";
import {hasData} from "@/statistics/utils/format";
import {AdministrationController} from "@/administration/controllers/AdministrationController";
import {cell} from "@/administration/utils";

const RANGES = [{value: '24h', label: '24 h'}, {value: '7d', label: '7 jours'}, {value: '30d', label: '30 jours'}, {value: '90d', label: '90 jours'}];

const NOTES = {
  business: "Les utilisateurs sont comptés par compte. Les connexions des super administrateurs et des robots ne sont pas comptées, aucune adresse IP n'est conservée.",
  website: "Visiteurs anonymes : aucun cookie, aucune adresse IP conservée. Un visiteur n'est reconnu que pour la journée, et ne l'est plus le lendemain.",
};

/**
 * Audience d'un front : visiteurs, sessions, appareils, navigateurs, heures de visite et rapidité d'affichage. Ni pages les plus vues ni parcours :
 * ils coûtaient trop de place et de mémoire.
 * app : l'audience du container, business (application) ou celle d'un site vitrine (website, oufleurcrea...) ; refreshedAt : change quand la page est actualisée, ce qui recharge aussi l'audience.
 */
export default {
  name: 'ContainerAudience',
  components: {StatisticsKpi, StatisticsCard, StatisticsChart, ActivityTable, RangeTabs},
  props: {
    app: {type: String, required: true},
    refreshedAt: {type: String, default: ''},
  },
  data() {
    return {
      RANGES,
      range: '7d',
      stats: undefined,
      hasFailed: false,
      requestId: 0,
    };
  },
  computed: {
    // Les sites vitrines (website, oufleurcrea...) comptent des visiteurs anonymes, comme le premier
    note() {
      return NOTES[this.app] ?? NOTES.website;
    },
    hasChartData() {
      return this.stats.chart.datasets.some(dataset => hasData(dataset.data));
    },
    // Appareils, navigateurs et systèmes en anneau ; les sites d'origine, plus nombreux, en barres
    breakdownCards() {
      return this.stats.breakdowns.map(breakdown => {
        const isBars = breakdown.id === 'referrers';
        return {
          id: breakdown.id,
          title: breakdown.title,
          subtitle: isBars ? "Sites d'où viennent les visiteurs (hors accès direct)" : 'Sessions',
          type: isBars ? 'bar' : 'doughnut',
          span: isBars ? 12 : 4,
          labels: breakdown.items.map(item => item.label),
          data: breakdown.items.map(item => item.count),
        };
      });
    },
  },
  watch: {
    refreshedAt() {
      this.load();
    },
    app() {
      this.stats = undefined;
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    cell, hasData,
    toneOf(tone) {
      return tone === 'warn' || tone === 'bad' ? tone : '';
    },
    setRange(range) {
      this.range = range;
      this.load();
    },
    async load() {
      // A late answer of an old request must not replace the last one
      const requestId = ++this.requestId;
      const resp = await AdministrationController.getAnalytics(this.app, this.range);
      if (requestId !== this.requestId) return;
      this.hasFailed = !resp || !!resp.errors;
      if (!this.hasFailed) this.stats = resp;
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/statistics/style/statistics.styl';

.audience
  margin-top 6px

.head
  display flex
  flex-wrap wrap
  align-items center
  justify-content space-between
  gap 8px 16px
  margin-bottom 8px

.section-title
  margin 0
  font-size 17px
  color THEME_COLOR_PRIMARY

.note
  margin 0 0 14px
  padding 8px 12px
  font-size 12.5px
  color CTRL_TEXT_MUTED
  background CTRL_BG
  border-radius 10px

.notice
  padding 28px 16px
  text-align center
  color CTRL_TEXT_MUTED
  background CTRL_BG
  border 1px dashed CTRL_BORDER
  border-radius 12px

.muted
  margin 0
  font-size 13.5px
  color CTRL_TEXT_MUTED

.facts
  display grid
  grid-template-columns repeat(auto-fill, minmax(170px, 1fr))
  gap 12px 20px
  margin 0

  dt
    font-size 12px
    color CTRL_TEXT_MUTED

  dd
    margin 2px 0 0
    font-size 13.5px
    font-weight 600
    color #3f3931
</style>
